import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Plus, Search, Edit, Eye, Trophy, Images } from "lucide-react"
import { useAllTournamentTypes } from "@/graphql/hooks/useAllTournamentTypes"
import { Skeleton } from "@/components/ui/skeleton"
import { TournamentTypeEditForm } from "./TournamentTypeEditForm"
import { TournamentTypeImagesModal } from "./TournamentTypeImagesModal"
import { useNavigate } from "react-router-dom"
import { AvatarUpload } from "../ui/avatar-upload"

export function TournamentTypesManagement() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedType, setSelectedType] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isImagesDialogOpen, setIsImagesDialogOpen] = useState(false)

  const { tournamentTypes, loading, error, refetch } = useAllTournamentTypes()

  const filteredTypes = tournamentTypes.filter((type) =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            Error cargando tipos de torneo: {error.message}
            <Button onClick={() => refetch()} className="ml-4" variant="outline">
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const handleEdit = (type) => {
    setSelectedType(type)
    setIsEditDialogOpen(true)
  }

  const handleManageImages = (type) => {
    setSelectedType(type)
    setIsImagesDialogOpen(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar tipos de torneo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={() => handleEdit(null)}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Tipo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Tipos de Torneo ({filteredTypes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead rowSpan={2}>Nombre</TableHead>
                <TableHead rowSpan={2}>Descripción</TableHead>
                <TableHead rowSpan={2}>Min Jugadores</TableHead>
                <TableHead rowSpan={2}>Multiplicador</TableHead>
                <TableHead colSpan={3} className="text-center">
                  Puntos
                </TableHead>
                <TableHead rowSpan={2}>Estado</TableHead>
                <TableHead rowSpan={2}>Torneos</TableHead>
                <TableHead rowSpan={2} className="text-right">
                  Acciones
                </TableHead>
              </TableRow>
              <TableRow>
                <TableHead>Victoria</TableHead>
                <TableHead>Empate</TableHead>
                <TableHead>Derrota</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTypes.map((type) => (
                <TableRow key={type.id}>
                  <TableCell className="font-medium">{type.name}</TableCell>
                  <TableCell className="max-w-xs">
                    {type.description || "Sin descripción"}
                  </TableCell>
                  <TableCell>{type.minPlayers || "N/A"}</TableCell>
                  <TableCell>{type.multiplier || "Sin puntos"}</TableCell>
                  <TableCell>{type.winnerPoints || 0}</TableCell>
                  <TableCell>{type.drawPoints || 0}</TableCell>
                  <TableCell>{type.loserPoints || 0}</TableCell>
                  <TableCell>
                    <Badge variant={type.status ? "default" : "secondary"}>
                      {type.status ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell>{type.totalTournaments || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigate(`/tournaments?typeId=${type.id}`)
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleManageImages(type)}>
                        <Images className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(type)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedType ? `Editar ${selectedType.name}` : "Nuevo Tipo de Torneo"}
            </DialogTitle>
          </DialogHeader>
          <TournamentTypeEditForm
            tournamentType={selectedType}
            onSuccess={() => {
              setIsEditDialogOpen(false)
              refetch()
            }}
            onCancel={() => setIsEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {selectedType && (
        <TournamentTypeImagesModal
          open={isImagesDialogOpen}
          onOpenChange={setIsImagesDialogOpen}
          tournamentType={selectedType}
          tournamentTypeName={selectedType.name}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  )
}
